// Журнал действий площадки над клубами: кто продлил подписку, отозвал терминал,
// поменял или заблокировал аккаунт. Читает только super_admin (GET /v1/audit).
//
// Зачем отдельно от клубного журнала: тот приезжает с терминала и описывает
// работу смены, а здесь — действия сотрудников площадки. Когда клуб спрашивает
// «почему у нас отключился терминал», ответ должен находиться за один взгляд.

import { useFetch } from '@/hooks/useApi'
import { AsyncState, SectionHead } from '@/components/ui'
import { dateTime } from '@/lib/format'

interface AuditRow {
  id: string
  actorEmail: string | null
  action: string
  clubId: string | null
  clubName: string | null
  targetType: string | null
  targetId: string | null
  meta: string | null
  createdAt: number
}

/** Человеческие названия действий (машинные коды — в meta и в API). */
const ACTION_LABELS: Record<string, string> = {
  'subscription.extend': 'Подписка продлена',
  'subscription.set_expiry': 'Установлен срок подписки',
  'subscription.suspend': 'Подписка приостановлена',
  'subscription.resume': 'Подписка возобновлена',
  'device.create': 'Выпущен токен терминала',
  'device.rename': 'Терминал переименован',
  'device.revoke': 'Терминал отозван',
  'device.restore': 'Терминал восстановлен',
  'device.delete': 'Терминал удалён',
  'device.approve_license': 'Одобрено продление терминала',
  'license_key.create': 'Выпущен лицензионный ключ',
  'license_key.revoke': 'Лицензионный ключ отозван',
  'staff.create': 'Добавлен сотрудник',
  'staff.update': 'Изменён сотрудник',
  'staff.disable': 'Сотрудник заблокирован',
  'staff.enable': 'Сотрудник разблокирован',
  'staff.delete': 'Сотрудник удалён',
  'plans.replace': 'Каталог тарифов обновлён из Help',
  'club.provision': 'Клуб подключён из Help'
}

/** Детали действия человекочитаемо: месяцы, новый срок, роль и т.п. */
function describeMeta(row: AuditRow): string {
  if (!row.meta) return ''
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(row.meta) as Record<string, unknown>
  } catch {
    return row.meta.slice(0, 120)
  }
  const parts: string[] = []
  if (typeof parsed.months === 'number') parts.push(`+${parsed.months} мес.`)
  if (typeof parsed.expiresAt === 'number') parts.push(`до ${dateTime(parsed.expiresAt)}`)
  if (typeof parsed.devices === 'number') parts.push(`терминалов: ${parsed.devices}`)
  if (typeof parsed.name === 'string') parts.push(`имя: ${parsed.name}`)
  if (typeof parsed.email === 'string') parts.push(parsed.email)
  if (typeof parsed.role === 'string') parts.push(`роль: ${parsed.role}`)
  if (parsed.passwordReset === true) parts.push('сброшен пароль')
  if (typeof parsed.version === 'string') parts.push(`версия ${parsed.version}`)
  if (typeof parsed.changed === 'number') parts.push(`изменено строк: ${parsed.changed}`)
  return parts.join(' · ')
}

export function AuditLog({
  clubId,
  limit
}: {
  clubId?: string
  limit?: number
}): React.JSX.Element {
  const list = useFetch<AuditRow[]>('/v1/audit', { clubId, limit }, [clubId, limit])
  const rows = list.data ?? []

  return (
    <section className="detail-block">
      <SectionHead
        title="Журнал действий"
        meta={rows.length > 0 ? `последние ${rows.length}` : undefined}
      />
      <AsyncState query={list} isEmpty={rows.length === 0} empty="Действий пока не было." />
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Кто</th>
                <th>Действие</th>
                {!clubId && <th>Клуб</th>}
                <th>Детали</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{dateTime(r.createdAt)}</td>
                  <td>{r.actorEmail ?? <span className="muted">—</span>}</td>
                  <td>{ACTION_LABELS[r.action] ?? r.action}</td>
                  {!clubId && <td>{r.clubName ?? <span className="muted">—</span>}</td>}
                  <td className="muted" style={{ fontSize: 12.5 }}>
                    {describeMeta(r)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
